import React, { useState } from "react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { useLang, loc } from "../i18n/LanguageContext";
import { api, fileUrl } from "../lib/api";
import { useContent } from "../lib/useContent";
import { useSeo, Icon } from "../components/common";
import { PageHero } from "../components/PageHero";
import { IMG } from "../data/site";

export default function Quote() {
  const { t, lang } = useLang();
  useSeo("Request a Free Quote | Sparko Cleaning Netherlands", "Request a free, tailored cleaning quote from Sparko within 24 hours. Fully insured, certified staff across the Netherlands.");
  const { data: services } = useContent("services");
  const [form, setForm] = useState({ name: "", company: "", phone: "", email: "", address: "", service: "", frequency: "", preferred_date: "", message: "" });
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    for (const f of files) {
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        setPhotos((p) => [...p, data.url]);
      } catch { toast.error("Upload failed for " + f.name); }
    }
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/submissions", { type: "quote", ...form, photos });
      setDone(true);
      toast.success(t.form.success);
    } catch { toast.error(t.form.error); }
    setSending(false);
  };

  const freqOptions = [["once", t.freq.once], ["daily", t.freq.daily], ["weekly", t.freq.weekly], ["monthly", t.freq.monthly]];

  if (done) return (
    <div>
      <PageHero title={t.quotePage.title} crumb={t.nav.quote} bg={IMG.hotel} />
      <div className="mx-auto max-w-xl px-5 py-28 text-center">
        <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} className="mx-auto grid place-items-center h-20 w-20 rounded-full bg-blue text-white"><Icon name="Check" size={40} /></motion.div>
        <h2 className="mt-6 font-display text-3xl text-navy">{t.form.success}</h2>
        <a href="/" className="mt-8 inline-flex rounded-full bg-navy px-6 py-3 text-white font-semibold">Home</a>
      </div>
    </div>
  );

  const inputCls = "w-full rounded-xl border border-black/10 px-4 py-3 outline-none focus:border-blue bg-white";

  return (
    <div>
      <PageHero title={t.quotePage.title} crumb={t.nav.quote} bg={IMG.hotel} sub={t.quotePage.sub} />
      <section className="mx-auto max-w-3xl px-5 md:px-8 py-16">
        <form onSubmit={submit} className="rounded-3xl bg-white p-8 border border-black/5 shadow-lg space-y-5" data-testid="quote-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div><label className="text-sm font-medium text-navy">{t.form.name} *</label><input required value={form.name} onChange={set("name")} className={inputCls} data-testid="q-name" /></div>
            <div><label className="text-sm font-medium text-navy">{t.form.company}</label><input value={form.company} onChange={set("company")} className={inputCls} data-testid="q-company" /></div>
            <div><label className="text-sm font-medium text-navy">{t.form.phone} *</label><input required value={form.phone} onChange={set("phone")} className={inputCls} data-testid="q-phone" /></div>
            <div><label className="text-sm font-medium text-navy">{t.form.email} *</label><input required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="q-email" /></div>
          </div>
          <div><label className="text-sm font-medium text-navy">{t.form.address}</label><input value={form.address} onChange={set("address")} className={inputCls} data-testid="q-address" /></div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <label className="text-sm font-medium text-navy">{t.form.service}</label>
              <select value={form.service} onChange={set("service")} className={inputCls} data-testid="q-service">
                <option value="">—</option>
                {services.map((s) => <option key={s.id} value={loc(s, "name", lang)}>{loc(s, "name", lang)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium text-navy">{t.form.frequency}</label>
              <select value={form.frequency} onChange={set("frequency")} className={inputCls} data-testid="q-frequency">
                <option value="">—</option>
                {freqOptions.map(([v, l]) => <option key={v} value={l}>{l}</option>)}
              </select>
            </div>
          </div>
          <div><label className="text-sm font-medium text-navy">{t.form.date}</label><input type="date" value={form.preferred_date} onChange={set("preferred_date")} className={inputCls} data-testid="q-date" /></div>
          <div><label className="text-sm font-medium text-navy">{t.form.message}</label><textarea rows={4} value={form.message} onChange={set("message")} className={`${inputCls} resize-none`} data-testid="q-message" /></div>

          <div>
            <label className="text-sm font-medium text-navy">{t.form.photos} <span className="text-slate-400">({t.form.optional})</span></label>
            <label className="mt-2 flex flex-col items-center justify-center gap-2 rounded-2xl border-2 border-dashed border-black/10 p-6 cursor-pointer hover:border-blue transition-colors" data-testid="q-upload-label">
              <Icon name={uploading ? "Loader2" : "ImagePlus"} size={24} className={`text-blue ${uploading ? "animate-spin" : ""}`} />
              <span className="text-sm text-slate-500">Click to upload photos</span>
              <input type="file" accept="image/*" multiple className="hidden" onChange={onFiles} data-testid="q-upload" />
            </label>
            {photos.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-3">
                {photos.map((p, i) => (
                  <div key={i} className="relative h-20 w-20 rounded-xl overflow-hidden">
                    <img src={fileUrl(p)} alt="" className="h-full w-full object-cover" />
                    <button type="button" onClick={() => setPhotos(photos.filter((_, idx) => idx !== i))}
                      className="absolute top-1 end-1 grid place-items-center h-5 w-5 rounded-full bg-navy/80 text-white"><Icon name="X" size={12} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <button type="submit" disabled={sending || uploading} data-testid="quote-submit"
            className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-blue px-7 py-4 font-semibold text-white hover:bg-[#0052FF] disabled:opacity-60">
            {sending ? <Icon name="Loader2" size={18} className="animate-spin" /> : <Icon name="Send" size={18} />}
            {sending ? t.form.sending : t.cta.send}
          </button>
        </form>
      </section>
    </div>
  );
}
